"use strict";

import { of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { ofType } from "redux-observable";
import { mergeMap, catchError, map, concatMap } from "rxjs/operators";

import {
  secureEmailSendInitiateComplete,
  secureEmailSendInitiateError,
  secureEmailSendMailComplete,
  secureEmailSendMailError,
  secureEmailSaveToDraftComplete,
  secureEmailSaveToDraftError,
  secureMailListComplete,
  secureMailDeleteMailComplete,
  secureDeleteMailError,
  secureMailListError,
  secureMailMessageListComplete,
  secureMailMessageListError,
  ajaxError,
  secureMailReplyComplete,
  secureMailReplyError,
  secureMailSingleNotifyComplete,
  secureMailSingleNotifyError,
  secureMailNotifyComplete,
  secureMailSingleError,
  secureMailFileUploadComplete,
  secureMailFileUploadError,
  secureMailFileDeleteComplete,
  secureMailFileDeleteError,
  secureMailFileDownloadComplete,
  secureMailFileDownloadError,
  secureMailSendOtpComplete,
  secureMailSendOtpError,
  secureMailResendOtpComplete,
  secureMailResendOtpError,
  secureMailMessageClosedComplete,
  secureMailMessageClosedError,
  notificationUnreadCountRequest,
  hideCustomeError,
  dashboardSecureMailNotificationClear,
} from "../_actions";

import {
  secureEmailConstants,
  commonConstants,
  responseCodes,
} from "../_constants";
import { getRequestHeader } from "../../_helpers/request.header";
import { downloadBlobFile } from "../../_helpers/support.services";
import i18n from "../../i18n";

export const secureEmailSendInitiateEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_EMAIL_SEND_INITIATE_REQUEST),
    mergeMap((action) => {
      const { mailSend, setShowOtpModal, ...payload } = action.payload;
      return ajax
        .post(
          "/api/secureMail/sendInitiate",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          concatMap((ajaxResponse) => {
            if (ajaxResponse.response?.data?.isOtpRequired) {
              setShowOtpModal?.(true);
            } else {
              mailSend?.();
            }

            return of(
              hideCustomeError(responseCodes.OTP_FAILED),
              hideCustomeError(responseCodes.OTP_REQUEST_LIMIT_REACHED),
              hideCustomeError(responseCodes.OTP_EXPIRED),
              secureEmailSendInitiateComplete(ajaxResponse.response),
            );
          }),
          catchError((error) => {
            if (
              error.response.statusCode ===
              responseCodes.OTP_REQUEST_LIMIT_REACHED
            ) {
              setShowOtpModal?.(true);
            }
            return of(
              secureEmailSendInitiateError(error?.response?.errorResponse),
              ajaxError(error),
            );
          }),
        );
    }),
  );

export const secureEmailSendMailEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_EMAIL_SEND_REQUEST),
    mergeMap((action) => {
      const { setShowSentSuccesMessage, setShowOtpModal, ...payload } =
        action.payload;
      return ajax
        .post("/api/secureMail/saveNewMail", payload, getRequestHeader("POST"))
        .pipe(
          map((ajaxResponse) => {
            setShowSentSuccesMessage?.(true);
            setShowOtpModal?.(false);
            return secureEmailSendMailComplete(ajaxResponse.response);
          }),
          catchError((error) => {
            if (
              error.response?.errorResponse?.responseCode ==
                responseCodes.OTP_FAILED ||
              error.response?.errorResponse?.responseCode ==
                responseCodes.OTP_EXPIRED ||
              error.response?.errorResponse?.responseCode ==
                responseCodes.OTP_BLOCKED
            ) {
              return of(ajaxError(error));
            }
            return of(secureEmailSendMailError(error));
          }),
        );
    }),
  );

export const secureEmailSaveToDraftEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_EMAIL_SAVE_TO_DRAFT_REQUEST),
    mergeMap((action) => {
      const { setShowDraftSavedSuccess, ...payload } = action.payload;

      return ajax
        .post("/api/secureMail/saveToDraft", payload, getRequestHeader("POST"))
        .pipe(
          map((ajaxResponse) => {
            setShowDraftSavedSuccess?.(true);
            return secureEmailSaveToDraftComplete(ajaxResponse.response);
          }),
          catchError((error) =>
            of(secureEmailSaveToDraftError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const secureMailGettListEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_LIST_REQUEST),
    mergeMap((action) => {
      const { isDeleted, isDraft, isAppend, ...payload } = action.payload;

      return ajax
        .post(
          "/api/secureMail/searchMail",
          { isDeleted, isDraft, ...payload },
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            secureMailListComplete({
              ...ajaxResponse.response,
              isDeleted,
              isDraft,
              isAppend,
            }),
          ),
          catchError((error) =>
            of(secureMailListError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const secureMailDeleteMailEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_DELETE_REQUEST),
    mergeMap((action) => {
      const {
        setShowDeleteRestoreSuccess,
        restoreAction,
        setShowDraftDeleteSuccesMessage,
        ...payload
      } = action.payload;

      return ajax
        .post(
          "/api/secureMail/deleteRestoreMail",
          payload,
          getRequestHeader("POST"),
        )
        .pipe(
          mergeMap((ajaxResponse) => {
            restoreAction?.();
            setShowDeleteRestoreSuccess?.(true);
            setShowDraftDeleteSuccesMessage?.(true);
            return of(secureMailDeleteMailComplete(ajaxResponse.response));
          }),
          catchError((error) =>
            of(secureDeleteMailError(error), ajaxError(error)),
          ),
        );
    }),
  );
export const secureMailMessageListMailEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_MESSAGE_LIST_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/secureMail/messageList",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            secureMailMessageListComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(secureMailMessageListError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const secureMailReplyEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_REPLY_REQUEST),
    mergeMap((action) => {
      const { showDraftSuccessMessage, ...payload } = action.payload;
      return ajax
        .post("/api/secureMail/messageReply", payload, getRequestHeader("POST"))
        .pipe(
          mergeMap((ajaxResponse) => {
            showDraftSuccessMessage?.();
            return of(
              // secureMailMessageListRequest({
              // 	secureMailId: payload.secureMailId,
              // }),
              secureMailReplyComplete(ajaxResponse.response),
            );
          }),
          catchError((error) =>
            of(secureMailReplyError(error), ajaxError(error)),
          ),
        );
    }),
  );
export const secureMailSingleNotifyEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_SINGLE_NOTIFY_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/secureMail/notifySingleMail",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            secureMailSingleNotifyComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(secureMailSingleNotifyError(error), ajaxError(error)),
          ),
        );
    }),
  );
export const secureMailNotifyEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_NOTIFY_REQUEST),
    mergeMap((action) =>
      ajax
        .post(
          "/api/secureMail/notifyMail",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          mergeMap((ajaxResponse) =>
            of(
              secureMailNotifyComplete(ajaxResponse.response),
              notificationUnreadCountRequest({ locale: i18n.language }),
            ),
          ),
          catchError((error) =>
            of(secureMailSingleError(error), ajaxError(error)),
          ),
        ),
    ),
  );

export const secureMailFileUploadEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_FILE_UPLOAD_REQUEST),
    mergeMap((action) => {
      const { file, secureMailMessageId, secureMailId, id, restoreAction } =
        action.payload;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("secureMailMessageId", secureMailMessageId);
      formData.append("secureMailId", secureMailId);

      return ajax
        .post(
          "/api/secureMail/attachment",
          formData,
          getRequestHeader(commonConstants.REQUEST_HEADER_METHOD_UPLOAD),
        )
        .pipe(
          map((ajaxResponse) => {
            restoreAction?.();
            return secureMailFileUploadComplete({
              ...ajaxResponse.response,
              id,
            });
          }),
          catchError((error) =>
            of(secureMailFileUploadError({ id }), ajaxError(error)),
          ),
        );
    }),
  );
export const secureMailFileDeleteEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_FILE_DELETE_REQUEST),
    mergeMap((action) => {
      const { saveToDraft, ...payload } = action.payload;

      return ajax
        .post(
          "/api/secureMail/attachment/delete",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) => {
            saveToDraft();
            return secureMailFileDeleteComplete(ajaxResponse.response);
          }),
          catchError((error) =>
            of(secureMailFileDeleteError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const secureMailFileDownloadEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_FILE_DOWNLOAD_REQUEST),
    mergeMap((action) => {
      return ajax({
        body: action.payload,
        url: "/api/secureMail/attachment/download",
        method: "POST",
        responseType: "blob",
        headers: getRequestHeader(commonConstants.REQUEST_HEADER_METHOD_UPLOAD),
      }).pipe(
        concatMap((ajaxResponse) => {
          downloadBlobFile(
            action?.payload?.name + "." + action?.payload?.type,
            ajaxResponse.response,
          );

          return of(
            secureMailFileDownloadComplete({
              status: "success",
            }),
          );
        }),
        catchError((error) =>
          of(secureMailFileDownloadError(error), ajaxError(error)),
        ),
      );
    }),
  );

export const secureMailSendOtpEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_SEND_OTP_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/secureMail/sendOtp",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            secureMailSendOtpComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(secureMailSendOtpError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const secureMailResendOtpEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_RESEND_OTP_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/secureMail/resendOtp",
          action.payload ?? {},
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            secureMailResendOtpComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(secureMailResendOtpError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const secureMailMessageClosedEpic = (actions$) =>
  actions$.pipe(
    ofType(secureEmailConstants.SECURE_MAIL_MESSAGE_CLOSED_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/secureMail/messageClosed",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          concatMap((ajaxResponse) =>
            of(
              secureMailMessageClosedComplete(ajaxResponse.response),

              dashboardSecureMailNotificationClear(),
            ),
          ),
          catchError((error) =>
            of(secureMailMessageClosedError(error), ajaxError(error)),
          ),
        );
    }),
  );
